import { setTrackUpdatePayload, requestId } from "../api/trackPayloads";
import Alert from "@mui/material/Alert";
import TableBody from "@mui/material/TableBody";
import TableFooter from "@mui/material/TableFooter";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import Table from "@mui/material/Table";
import { useDialogs } from "../ui/DialogProvider";
import { useForm } from "react-hook-form";
import useFormField from "../hooks/useFormField";
import TextInput from "../ui/TextInput";
import Button from "@mui/material/Button";
import React from "react";
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from "@dnd-kit/modifiers";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { API_BASE, fetchJson, apiFetch } from "../utils";
import { moveSetTrack } from "../utils/setTracks";
import SortableSetTrackRow from "../components/SortableSetTrackRow";
import SortableSetTrackCard from "../components/SortableSetTrackCard";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import SetOptionsMenu from "../components/SetOptionsMenu";
const setAutoScroll = {
  canScroll: (element) => element.classList.contains("set-tracklist"),
};
export default function SetDetails() {
  const { setId } = useParams();
  return <SetDetailsContent key={setId} setId={setId} />;
}
function SetDetailsContent({ setId }) {
  const dialogs = useDialogs();
  const form = useForm({
    defaultValues: {
      newName: "",
    },
    mode: "onChange",
  });
  const navigate = useNavigate();
  const location = useLocation();
  const [info, setInfo] = React.useState(null);
  const [tracks, setTracks] = React.useState([]);
  const [error, setError] = React.useState(null);
  const [mutationError, setMutationError] = React.useState(null);
  const [revision, setRevision] = React.useState(0);
  const [pending, setPending] = React.useState(false);
  const busy = React.useRef(false);
  const active = React.useRef(true);
  const [renaming, setRenaming] = React.useState(false);
  const [newName, setNewName] = useFormField(form, "newName");
  const fileInputRef = React.useRef(null);
  const [imgVersion, setImgVersion] = React.useState(Date.now());
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  React.useEffect(() => {
    const controller = new AbortController();
    active.current = true;
    setError(null);
    setInfo(null);
    setTracks([]);
    Promise.all([
      fetchJson(`${API_BASE}/user-sets/${setId}/`, {
        signal: controller.signal,
      }),
      fetchJson(`${API_BASE}/user-sets/${setId}/tracks/`, {
        signal: controller.signal,
      }),
    ])
      .then(([data, trackData]) => {
        if (controller.signal.aborted) return;
        if (!data || typeof data.name !== "string")
          throw new Error("Invalid set response");
        const nextTracks = Array.isArray(trackData?.tracks)
          ? trackData.tracks
          : Array.isArray(trackData)
            ? trackData
            : data.tracks;
        if (!Array.isArray(nextTracks))
          throw new Error("Invalid set tracks response");
        setInfo({
          id: data.id,
          name: data.name,
          description: data.description,
        });
        setTracks(nextTracks);
      })
      .catch((failure) => {
        if (!controller.signal.aborted) setError(failure);
      });
    return () => {
      active.current = false;
      controller.abort();
    };
  }, [setId, revision]);
  const beginMutation = () => {
    if (busy.current) return false;
    busy.current = true;
    setPending(true);
    setMutationError(null);
    return true;
  };
  const endMutation = () => {
    busy.current = false;
    if (active.current) setPending(false);
  };
  const rename = async (name) => {
    if (!info || !beginMutation()) return;
    try {
      await fetchJson(`${API_BASE}/user-sets/${setId}/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          name,
        }),
      });
      setInfo((i) => ({
        ...i,
        name,
      }));
      setRenaming(false);
    } catch (err) {
      setMutationError(err);
    } finally {
      endMutation();
    }
  };
  const startRename = () => {
    if (!info || busy.current) return;
    setNewName(info.name);
    setRenaming(true);
  };
  const finishRename = () => {
    const trimmed = newName.trim();
    if (busy.current || !info) return;
    if (!trimmed) {
      setMutationError(new Error("Set name cannot be empty."));
      return;
    }
    if (trimmed === info.name) {
      setRenaming(false);
      return;
    }
    rename(trimmed);
  };
  const removeTrack = async (id) => {
    if (
      busy.current ||
      !(await dialogs.confirm("Remove track?")) ||
      !beginMutation()
    )
      return;
    try {
      await fetchJson(`${API_BASE}/user-sets/${setId}/tracks/`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          set_track_id: requestId(id, "Set track ID"),
        }),
      });
      setTracks((t) => t.filter((tr) => tr.id !== id));
    } catch (err) {
      setMutationError(err);
    } finally {
      endMutation();
    }
  };
  const updateTrack = async (setTrackId, fields) => {
    if (!beginMutation())
      throw new Error("Wait for the current set change to finish.");
    try {
      const payload = setTrackUpdatePayload(fields);
      await fetchJson(`${API_BASE}/user-sets/${setId}/tracks/`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          ...payload,
          id: requestId(setTrackId, "Set track ID"),
        }),
      });
      setTracks((t) =>
        t.map((tr) =>
          tr.id === setTrackId
            ? {
                ...tr,
                ...payload,
              }
            : tr,
        ),
      );
    } finally {
      endMutation();
    }
  };
  const reorderTracks = async (from, to) => {
    if (
      from === to ||
      from < 0 ||
      to < 0 ||
      from >= tracks.length ||
      to >= tracks.length
    ) {
      return;
    }
    if (!beginMutation()) return;
    const previous = tracks;
    const newOrder = moveSetTrack(tracks, from, to);
    setTracks(newOrder);
    try {
      await fetchJson(`${API_BASE}/user-sets/${setId}/reorder/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          track_ids: newOrder.map((t) => requestId(t.track?.id ?? t.track_id)),
        }),
      });
    } catch (err) {
      setTracks(previous);
      setMutationError(
        new Error(
          `Could not save track order: ${err.message}. The previous order has been restored.`,
        ),
      );
    } finally {
      endMutation();
    }
  };
  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = tracks.findIndex((t) => t.id === active.id);
    const to = tracks.findIndex((t) => t.id === over.id);
    reorderTracks(from, to);
  };
  const checkImageExists = async () => {
    const resp = await apiFetch(`${API_BASE}/user-sets/${setId}/image/`, {
      method: "GET",
      redirect: "manual",
      credentials: "include",
    });
    if (resp.status === 404) return false;
    if (
      resp.ok ||
      resp.type === "opaqueredirect" ||
      (resp.status >= 300 && resp.status < 400)
    )
      return true;
    throw new Error(
      `Could not check the existing image (HTTP ${resp.status}).`,
    );
  };
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !beginMutation()) return;
    const input = e.target;
    try {
      const exists = await checkImageExists();
      if (!active.current) return;
      if (exists) {
        const confirmOverwrite = await dialogs.confirm(
          "Are you sure you want to overwrite the existing image?",
        );
        if (!confirmOverwrite) {
          return;
        }
      }
      const form = new FormData();
      form.append("image", file);
      await fetchJson(
        `${API_BASE}/user-sets/${setId}/image/${exists ? "?overwrite=true" : ""}`,
        {
          method: "POST",
          body: form,
          credentials: "include",
        },
      );
      setImgVersion(Date.now());
    } catch (err) {
      setMutationError(err);
    } finally {
      input.value = "";
      endMutation();
    }
  };
  const deleteSet = async () => {
    if (
      busy.current ||
      !(await dialogs.confirm("Delete this set?")) ||
      !beginMutation()
    )
      return;
    try {
      await fetchJson(`${API_BASE}/user-sets/${setId}/`, {
        method: "DELETE",
        credentials: "include",
      });
      if (active.current) navigate("/sets");
    } catch (err) {
      setMutationError(err);
    } finally {
      endMutation();
    }
  };
  if (error)
    return (
      <Alert role="alert" severity="error">
        Error loading set: {error.message}{" "}
        <Button
          className="btn"
          onClick={() => setRevision((value) => value + 1)}
        >
          Retry
        </Button>
      </Alert>
    );
  if (!info) return <div role="status">Loading...</div>;
  return (
    <div className="set-page" aria-busy={pending}>
      {mutationError && (
        <Alert role="alert" severity="error">
          {mutationError.message}
        </Alert>
      )}
      <section className="set-meta">
        <img
          className="set-image"
          src={`${API_BASE}/user-sets/${setId}/image/?v=${imgVersion}`}
          alt="set art"
          width={300}
          height={300}
        />
        <input
          type="file"
          aria-label="Set image"
          disabled={pending}
          accept="image/*"
          style={{
            display: "none",
          }}
          ref={fileInputRef}
          onChange={handleFileChange}
        />
        {renaming ? (
          <TextInput
            className="set-edit-input"
            aria-label="Set name"
            disabled={pending}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onBlur={form.handleSubmit(finishRename)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.target.blur();
              }
              if (e.key === "Escape") setRenaming(false);
            }}
            autoFocus
            multiline
          />
        ) : (
          <h2 className="set-title">{info.name}</h2>
        )}
        <SetOptionsMenu
          onRename={startRename}
          onDelete={deleteSet}
          onChangeImage={() =>
            fileInputRef.current && fileInputRef.current.click()
          }
        />
      </section>
      <div className="tracklist set-tracklist">
        <DndContext
          autoScroll={setAutoScroll}
          modifiers={[restrictToParentElement, restrictToVerticalAxis]}
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <Table className="track-table set-track-table">
            <TableHead>
              <TableRow>
                <TableCell component="th" />
                <TableCell component="th">#</TableCell>
                <TableCell component="th">Title</TableCell>
                <TableCell component="th">Position</TableCell>
                <TableCell component="th">BPM</TableCell>
                <TableCell component="th">Duration</TableCell>
                <TableCell component="th">Notes</TableCell>
                <TableCell component="th" />
              </TableRow>
            </TableHead>
            <TableBody>
              <SortableContext
                items={tracks.map((t) => t.id)}
                strategy={verticalListSortingStrategy}
              >
                {tracks.map((t, i) => (
                  <SortableSetTrackRow
                    key={t.id || i}
                    index={i}
                    setTrack={t}
                    onRemove={() => removeTrack(t.id)}
                    onUpdate={updateTrack}
                    onNavigate={() =>
                      navigate(
                        `/album/${t.track?.album?.id ?? t.track?.album ?? t.album?.id ?? t.album ?? t.album_id}`,
                      )
                    }
                  />
                ))}
              </SortableContext>
            </TableBody>
            <TableFooter>
              <TableRow className="track-row add-track-row">
                <TableCell
                  colSpan={8}
                  style={{
                    textAlign: "center",
                  }}
                >
                  <Button
                    className="btn"
                    disabled={pending}
                    onClick={() =>
                      navigate(
                        `/add-track?set=${setId}&return=${encodeURIComponent(location.pathname + location.search)}`,
                      )
                    }
                  >
                    + Add Track
                  </Button>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </DndContext>
        <div className="track-cards">
          <DndContext
            autoScroll={setAutoScroll}
            modifiers={[restrictToParentElement, restrictToVerticalAxis]}
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <div className="set-sortable-cards">
              <SortableContext
                items={tracks.map((t) => t.id)}
                strategy={verticalListSortingStrategy}
              >
                {tracks.map((t, i) => (
                  <SortableSetTrackCard
                    key={t.id || i}
                    setTrack={t}
                    onUpdate={updateTrack}
                    onRemove={() => removeTrack(t.id)}
                    onNavigate={() =>
                      navigate(
                        `/album/${t.track?.album?.id ?? t.track?.album ?? t.album?.id ?? t.album ?? t.album_id}`,
                      )
                    }
                  />
                ))}
              </SortableContext>
            </div>
          </DndContext>
          <Button
            className="track-card add-track-card"
            disabled={pending}
            onClick={() =>
              navigate(
                `/add-track?set=${setId}&return=${encodeURIComponent(location.pathname + location.search)}`,
              )
            }
          >
            + Add Track
          </Button>
        </div>
      </div>
    </div>
  );
}
